import { TFunction } from 'i18next'

/**
 * GWR (Gebäude- und Wohnungsregister) enum types
 *
 * - buildingCategory: GKAT - Gebäudekategorie (6 values)
 * - buildingClass: GKLAS - Gebäudeklasse (25+ values)
 * - buildingStatus: GSTAT - Gebäudestatus (7 values)
 *
 * @see https://www.housing-stat.ch/de/help/42.html (GKAT)
 * @see https://www-r.housing-stat.ch/de/help/gklas.html (GKLAS)
 * @see https://opendata.swiss/de/dataset/eidg-gebaude-und-wohnungsregister-gebaudestatus (GSTAT)
 */
export type GwrEnumType =
  | 'buildingCategory'
  | 'buildingClass'
  | 'buildingStatus'
  | 'energySource'
  | 'heaterGenerator'

/**
 * Translates a GWR enum value to the user's language.
 *
 * Uses the official Swiss Federal Statistical Office (FSO) translations
 * for building category (GKAT), building class (GKLAS), and building status (GSTAT).
 *
 * The API returns values in French - this function translates them to
 * the user's selected language (EN, DE, IT, or back to FR).
 *
 * @param value - The GWR enum value (in French from API)
 * @param enumType - The type of GWR enum ('buildingCategory', 'buildingClass', 'buildingStatus')
 * @param t - The i18next translation function
 * @returns The translated value, or the original value if no translation exists
 *
 * @example
 * // Translate building status
 * translateGwrValue('existant', 'buildingStatus', t) // Returns "Existing" in English
 *
 * @example
 * // Translate building class
 * translateGwrValue('Maison individuelle', 'buildingClass', t) // Returns "Single-family house" in English
 *
 * @example
 * // Graceful fallback for unknown values
 * translateGwrValue('Unknown Value', 'buildingCategory', t) // Returns "Unknown Value"
 */
export function translateGwrValue(
  value: string | undefined | null,
  enumType: GwrEnumType,
  t: TFunction
): string {
  if (!value) return 'N/A'
  // Use explicit 'ns' option instead of 'gwr:' prefix
  // This works regardless of nsSeparator configuration in i18next
  const translationKey = `${enumType}.${value}`
  return t(translationKey, { ns: 'gwr', defaultValue: value })
}
